import { AppRouteMeta } from '@sennder/shell-utilities'
import {
  ApplicationTypes,
  getRenderer as getRegistryRenderer,
  MFRepositories,
} from '@sennder/microfrontend-registry'
import {
  canAccessMyAnalytics,
  canAccessNewShipment,
  canAccessOpportunities,
  canAccessOperationsOverview,
  canAccessMyCargos,
} from './user-access'

/**
 * Unique application route
 */
export type AppRoute = keyof typeof routes

export const getRenderer = <
  TRepo extends MFRepositories,
  TApp extends keyof ApplicationTypes[TRepo]
>(
  Repo: TRepo,
  App: TApp
) =>
  getRegistryRenderer<TRepo, TApp, unknown>(Repo, App, {
    // No behaviour depends on it - rename only for debug-logs purposes.
    integrator: 'otters-shell',
  })

const ottersTrips = getRenderer(MFRepositories.OttersTrips, 'Page')
const ottersMf = getRenderer(MFRepositories.OttersMF, 'page')
/**
 * All routes of the application, ordered alphabetically by path.
 * Each route has navigation guards, analytical and logging contexts.
 */
export const routes = {
  '/': {
    name: 'dashboard',
    renderer: ottersTrips,
    context: {
      analytics: {
        module: 'dashboard',
        submodule: '',
      },
      logger: {
        module: 'dashboard',
        codeOwners: 'shipper-portal-pod',
      },
    },
  },
  '/shipments': {
    name: 'my-shipments',
    renderer: ottersTrips,
    context: {
      analytics: {
        module: 'myShipments',
        submodule: '',
      },
      logger: {
        module: 'myShipments',
        codeOwners: 'shipper-portal-pod',
      },
    },
  },
  '/cargos': {
    name: 'my-cargos',
    renderer: ottersTrips,
    context: {
      analytics: {
        module: 'myCargos',
        submodule: '',
      },
      logger: {
        module: 'myCargos',
        codeOwners: 'shipper-portal-pod',
      },
    },
    guards: [canAccessMyCargos],
  },
  '/overview': {
    name: 'operations-overview',
    renderer: ottersTrips,
    context: {
      analytics: {
        module: 'operationsOverview',
        submodule: '',
      },
      logger: {
        module: 'operationsOverview',
        codeOwners: 'shipper-portal-pod',
      },
    },
    guards: [canAccessOperationsOverview],
  },
  '/booking': {
    name: 'new-shipment',
    renderer: ottersMf,
    context: {
      analytics: {
        module: 'newShipment',
        submodule: '',
      },
      logger: {
        module: 'newShipment',
        codeOwners: 'shipper-portal-pod',
      },
    },
    guards: [canAccessNewShipment],
  },
  '/opportunities': {
    name: 'opportunities',
    renderer: ottersMf,
    context: {
      analytics: {
        module: 'spotRequests',
        submodule: '',
      },
      logger: {
        module: 'spotRequests',
        codeOwners: 'shipper-portal-pod',
      },
    },
    guards: [canAccessOpportunities],
  },
  '/my-analytics': {
    name: 'my-analytics',
    renderer: getRenderer(MFRepositories.MyAnalytics, 'page'),
    context: {
      analytics: {
        module: 'myanalytics-mf',
        submodule: '',
      },
      logger: {
        module: 'myanalytics-mf',
        codeOwners: 'shipper-portal-pod',
      },
    },
    guards: [canAccessMyAnalytics],
  },
  '/success': {
    name: 'new-shipment-success',
    renderer: ottersMf,
    context: {
      analytics: {
        module: 'newShipment',
        submodule: '',
      },
      logger: {
        module: 'newShipment',
        codeOwners: 'shipper-portal-pod',
      },
    },
  },
} as const satisfies {
  [TRoute: string]: AppRouteMeta
}
